import type { LabApplied } from "./strategy-lab-types.js";

/**
 * The seven swing strategies the strategy lab reads (D17), in chip order.
 *
 * A second copy of `BACKTEST_STRATEGIES` and `LEVELLED_STRATEGIES` from
 * `@savi/core`, because shipped `apps/web` code cannot import core: its barrel
 * pulls `node:crypto` into the bundle (`content/wiki/thresholds.test.ts`
 * explains). `lab-strategies.test.ts` holds this copy to core's, so a renamed
 * or added strategy fails a test instead of a request.
 */
export const LAB_STRATEGY_IDS = [
  "prophet_swing",
  "ict_long",
  "ict_short",
  "ict_mss_bullish",
  "ict_mss_bearish",
  "ict_sweep_bullish",
  "ict_sweep_bearish",
] as const;
export type LabStrategyId = (typeof LAB_STRATEGY_IDS)[number];

export interface LabStrategy {
  id: LabStrategyId;
  label: string;
  /** Sets a stop and a target, which "its own exits" needs (D8). */
  levelled: boolean;
}

export const LAB_STRATEGIES: readonly LabStrategy[] = [
  { id: "prophet_swing", label: "Prophet swing", levelled: true },
  { id: "ict_long", label: "ICT long", levelled: true },
  { id: "ict_short", label: "ICT short", levelled: true },
  { id: "ict_mss_bullish", label: "ICT MSS bullish", levelled: false },
  { id: "ict_mss_bearish", label: "ICT MSS bearish", levelled: false },
  { id: "ict_sweep_bullish", label: "ICT sweep bullish", levelled: false },
  { id: "ict_sweep_bearish", label: "ICT sweep bearish", levelled: false },
];

/** An id this list does not know reads as levelled, so nothing is locked by a typo. */
export function isLevelled(strategy: string): boolean {
  return LAB_STRATEGIES.find((entry) => entry.id === strategy)?.levelled ?? true;
}

/**
 * The exit rule the server applies, which mirrors the route's `exitFor`: a
 * strategy with no stop reads on the 21-session hold whatever `?exit=` says.
 */
export function exitFor(strategy: string, exit: LabApplied["exit"]): LabApplied["exit"] {
  return isLevelled(strategy) ? exit : "hold21";
}
